import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  ASSIGNMENT_ACCEPTED,
  ASSIGNMENT_CLARIFICATION,
  ASSIGNMENT_PENDING_ACCEPTANCE,
} from '../../../../shared/contracts/backend-enums.js';

/**
 * Assign Job to Field Force.
 *
 * Three views over ONE authoritative read, GET /v1/vendor/inbox
 * (VendorSelfService.inbox). That endpoint derives the vendor from the login
 * and filters on a.vendor_id, so a vendor can only ever see his own work —
 * unlike /v1/painter/assignments/admin, which is tenant-wide and must never be
 * handed to a vendor.
 *
 * Accepting, rejecting and allocating hand off to the screens that already
 * own those rules; the one action taken here is taking up a line the workflow
 * released to this vendor, which makes it his job for those screens:
 *   take up   -> POST /v1/vendor/released-lines/:id/assignment, then accept
 *   accept    -> /vendor-ops/jobs/:id/accept    (VendorJobAcceptPage)
 *   reject    -> /vendor-ops/jobs/:id/reject    (VendorJobRejectPage — reason
 *                from Rejection Reason Master, remark mandatory)
 *   allocate  -> /vendor-ops/jobs/:id/painter   (VendorPainterPage)
 */

/** Mirrors backend InboxItem (VendorSelfService.inbox). */
export interface VendorInboxItem {
  /** Null for a line released to this vendor that he has not taken up yet. */
  readonly assignmentId: string | null;
  readonly requestLineId: string | null;
  readonly jobRequestId: string;
  readonly jobCode: string;
  readonly status: string;
  readonly priority: string;
  readonly territoryId: string;
  readonly acceptanceDueAt: string | null;
  readonly painterAssignmentStatus: string | null;
  readonly painterAssignmentActive: boolean;
  readonly painterUserId: string | null;
  readonly painterName: string | null;
  readonly painterCode: string | null;
  readonly painterMobileNumber: string | null;
  readonly painterRejectionReason: string | null;
  readonly painterRejectionRemarks: string | null;
  readonly painterRejectedAt: string | null;
}

interface InboxState {
  readonly rows: readonly VendorInboxItem[] | null;
  readonly error: string | null;
}

function useVendorInbox(): InboxState {
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly VendorInboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiRequest<{ items: readonly VendorInboxItem[] }>('/v1/vendor/inbox', { token });
        if (!Array.isArray(r.items)) throw new Error('Vendor inbox response did not contain an items list.');
        if (alive) { setRows(r.items); setError(null); }
      } catch (e) {
        if (alive) { setRows(null); setError((e as Error).message); }
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return { rows, error };
}

/** A painter holds the job only while the allocation is live. */
const isAllocated = (r: VendorInboxItem): boolean => r.painterAssignmentActive;
const rowId = (r: VendorInboxItem): string => r.assignmentId ?? r.requestLineId ?? r.jobRequestId;
const hasPainterHistory = (r: VendorInboxItem): boolean =>
  r.painterAssignmentStatus !== null || r.painterUserId !== null;

const painterLabel = (r: VendorInboxItem): string =>
  r.painterName ? `${r.painterName}${r.painterCode ? ` · ${r.painterCode}` : ''}` : (r.painterUserId ?? '—');

function Shell({ label, title, nav, children }: {
  label: string; title: string; nav: ReactNode; children: ReactNode;
}): ReactNode {
  return (
    <main aria-label={label}>
      <h1>{title}</h1>
      <nav aria-label="field-force-nav" className="tl-toolbar">{nav}</nav>
      {children}
    </main>
  );
}

function Guard({ state, empty, children }: {
  state: InboxState; empty: string; children: (rows: readonly VendorInboxItem[]) => ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  if (state.error) return <p role="alert" aria-label="ff-error">{state.error}</p>;
  if (state.rows === null) return <p aria-label="ff-loading">{t('common.loading')}</p>;
  return (
    <>
      {state.rows.length === 0 ? <p aria-label="ff-empty">{empty}</p> : children(state.rows)}
    </>
  );
}

// ── 1. Every job awarded to this vendor, with accept / reject ───────────────
export function VendorFieldForceJobsPage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const navigate = useNavigate();
  const state = useVendorInbox();
  const [takeUpError, setTakeUpError] = useState<string | null>(null);

  // A released line becomes this vendor's job here; the accept screen that
  // already owns acceptance then takes over.
  async function takeUp(requestLineId: string): Promise<void> {
    setTakeUpError(null);
    try {
      const r = await apiRequest<{ assignmentId: string }>(
        `/v1/vendor/released-lines/${requestLineId}/assignment`,
        { method: 'POST', token, body: {} },
      );
      navigate(`/vendor-ops/jobs/${r.assignmentId}/accept`);
    } catch (e) {
      setTakeUpError((e as Error).message);
    }
  }

  return (
    <Shell
      label="vendor-field-force"
      title={t('vops.ff.jobsTitle')}
      nav={
        <>
          <Link className="tl-btn tl-btn--primary" to="/vendor-ops/field-force/allocate" aria-label="ff-nav-allocate">
            {t('vops.ff.allocateBtn')}
          </Link>
          <Link className="tl-btn tl-btn--secondary" to="/vendor-ops/field-force/tracking" aria-label="ff-nav-tracking">
            {t('vops.ff.trackingTitle')}
          </Link>
        </>
      }
    >
      {takeUpError ? <p role="alert" aria-label="ff-take-up-error">{takeUpError}</p> : null}
      <Guard state={state} empty={t('vops.ff.noJobs')}>
        {(rows) => (
          <DataTable<VendorInboxItem>
            testId="ff-jobs-table"
            ariaLabel="ff-jobs-table"
            rowKey={rowId}
            rowAriaLabel={(r) => `ff-job-${rowId(r)}`}
            columns={[
              {
                key: 'job', header: t('vops.dashboard.colJob'),
                render: (r) => (r.assignmentId ? (
                  <Link to={`/vendor-ops/jobs/${r.assignmentId}`} aria-label={`ff-job-link-${r.assignmentId}`}>
                    {r.jobCode || r.jobRequestId}
                  </Link>
                ) : (r.jobCode || r.jobRequestId)),
              },
              { key: 'status', header: t('vops.common.status'), render: (r) => r.status },
              { key: 'priority', header: t('vops.common.priority'), render: (r) => r.priority || '—' },
              { key: 'due', header: t('vops.dashboard.colAcceptanceDue'), render: (r) => at(r.acceptanceDueAt) },
              {
                key: 'painter', header: t('vops.dashboard.colPainter'),
                render: (r) => (isAllocated(r) ? painterLabel(r) : t('vops.dashboard.painterUnassigned')),
              },
              {
                key: 'actions', header: t('vops.ff.colAction'),
                // Acceptance is open at PENDING_ACCEPTANCE; a clarification
                // thread may still be rejected, which is why the reject route
                // is offered there too (JobAcceptanceService.reject).
                render: (r) => (
                  <>
                    {!r.assignmentId && r.requestLineId ? (
                      <button type="button" className="tl-btn tl-btn--primary tl-btn--sm"
                              onClick={() => { void takeUp(r.requestLineId!); }}
                              aria-label={`ff-take-up-${r.requestLineId}`}>
                        {t('vops.jobDetail.actionAccept')}
                      </button>
                    ) : null}
                    {r.status === ASSIGNMENT_PENDING_ACCEPTANCE ? (
                      <Link className="tl-btn tl-btn--primary tl-btn--sm"
                            to={`/vendor-ops/jobs/${r.assignmentId}/accept`}
                            aria-label={`ff-accept-${r.assignmentId}`}>
                        {t('vops.jobDetail.actionAccept')}
                      </Link>
                    ) : null}
                    {r.status === ASSIGNMENT_PENDING_ACCEPTANCE || r.status === ASSIGNMENT_CLARIFICATION ? (
                      <Link className="tl-btn tl-btn--danger tl-btn--sm"
                            to={`/vendor-ops/jobs/${r.assignmentId}/reject`}
                            aria-label={`ff-reject-${r.assignmentId}`}>
                        {t('vops.jobDetail.actionReject')}
                      </Link>
                    ) : null}
                    {r.status === ASSIGNMENT_ACCEPTED && !isAllocated(r) ? (
                      <Link className="tl-btn tl-btn--primary tl-btn--sm"
                            to={`/vendor-ops/jobs/${r.assignmentId}/painter`}
                            aria-label={`ff-assign-${r.assignmentId}`}>
                        {t('vops.ff.assignBtn')}
                      </Link>
                    ) : null}
                  </>
                ),
              },
            ]}
            rows={rows}
          />
        )}
      </Guard>
    </Shell>
  );
}

// ── 2. Accepted, not yet with a painter ────────────────────────────────────
export function VendorFieldForceAllocatePage(): ReactNode {
  const { t } = useTranslation();
  const state = useVendorInbox();
  const pending = (state.rows ?? []).filter((r) => r.status === ASSIGNMENT_ACCEPTED && !isAllocated(r));
  const view: InboxState = { rows: state.rows === null ? null : pending, error: state.error };

  return (
    <Shell
      label="vendor-field-force-allocate"
      title={t('vops.ff.allocateTitle')}
      nav={
        <Link className="tl-btn tl-btn--secondary" to="/vendor-ops/field-force" aria-label="ff-nav-jobs">
          {t('vops.ff.backToJobs')}
        </Link>
      }
    >
      <Guard state={view} empty={t('vops.ff.noneToAllocate')}>
        {(rows) => (
          <DataTable<VendorInboxItem>
            testId="ff-allocate-table"
            ariaLabel="ff-allocate-table"
            rowKey={rowId}
            rowAriaLabel={(r) => `ff-allocate-${r.assignmentId}`}
            columns={[
              { key: 'job', header: t('vops.dashboard.colJob'), render: (r) => r.jobCode || r.jobRequestId },
              { key: 'priority', header: t('vops.common.priority'), render: (r) => r.priority || '—' },
              {
                key: 'action', header: t('vops.ff.colAction'),
                render: (r) => (
                  <Link className="tl-btn tl-btn--primary tl-btn--sm"
                        to={`/vendor-ops/jobs/${r.assignmentId}/painter`}
                        aria-label={`ff-allocate-btn-${r.assignmentId}`}>
                    {t('vops.ff.assignBtn')}
                  </Link>
                ),
              },
            ]}
            rows={rows}
          />
        )}
      </Guard>
    </Shell>
  );
}

// ── 3. Already with a painter: who holds it, and where it stands ───────────
export function VendorFieldForceTrackingPage(): ReactNode {
  const { t } = useTranslation();
  const state = useVendorInbox();
  const allocated = (state.rows ?? []).filter(hasPainterHistory);
  const view: InboxState = { rows: state.rows === null ? null : allocated, error: state.error };

  return (
    <Shell
      label="vendor-field-force-tracking"
      title={t('vops.ff.trackingTitle')}
      nav={
        <Link className="tl-btn tl-btn--secondary" to="/vendor-ops/field-force" aria-label="ff-nav-jobs">
          {t('vops.ff.backToJobs')}
        </Link>
      }
    >
      <Guard state={view} empty={t('vops.ff.noneAllocated')}>
        {(rows) => (
          <DataTable<VendorInboxItem>
            testId="ff-tracking-table"
            ariaLabel="ff-tracking-table"
            rowKey={rowId}
            rowAriaLabel={(r) => `ff-track-${r.assignmentId}`}
            columns={[
              {
                key: 'job', header: t('vops.dashboard.colJob'),
                render: (r) => (
                  <Link to={`/vendor-ops/jobs/${r.assignmentId}`} aria-label={`ff-track-link-${r.assignmentId}`}>
                    {r.jobCode || r.jobRequestId}
                  </Link>
                ),
              },
              { key: 'painter', header: t('vops.dashboard.colPainter'), render: (r) => painterLabel(r) },
              { key: 'mobile', header: t('vops.ff.colMobile'), render: (r) => r.painterMobileNumber ?? '—' },
              { key: 'status', header: t('vops.common.status'), render: (r) => r.status },
              {
                key: 'rejection', header: t('vops.ff.colPainterRejection'),
                render: (r) =>
                  r.painterRejectionReason
                    ? `${r.painterRejectionReason}${r.painterRejectionRemarks ? ` · ${r.painterRejectionRemarks}` : ''}`
                    : '—',
              },
            ]}
            rows={rows}
          />
        )}
      </Guard>
    </Shell>
  );
}
